/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * SettingsLabels component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.3.0
 */

import { memo, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import InfinityScrollWrapper from '@components/InfinityScrollWrapper/InfinityScrollWrapper';
import { LabelEditorList } from '@components/Table/components/footerModal/LabelEditor/components/LabelEditorList';
import LabelEditorSearch from '@components/Table/components/footerModal/LabelEditor/components/LabelEditorSearch';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import {
  makeSelectTranslationCommon,
  makeSelectTranslationPageSettings,
} from '@modules/internationalization/intSelectors';
import type { ICommonTranslation } from '@modules/internationalization/types';
import type { IPageSettingsTranslation } from '@modules/internationalization/types/pages/pageSettings';
import { makeSelectLabelsImportButtonEnabled } from '@modules/role/selectors/guardSelector';
import type { IRootReducer } from '@reducers/rootReducer';

import {
  PageSettingsContentStyled,
  PageSettingsHeaderStyled,
} from '../../styled/PageSettingsStyled';
import {
  addSettingsLabelAction,
  getSettingsLabelsListAction,
  removeSettingsLabelAction,
} from '../settingsLabelsActions';
import {
  makeSelectSettingsLabelsIsLoading,
  makeSelectSettingsLabelsList,
  makeSelectSettingsLabelsListHasMore,
} from '../settingsLabelsSelectors';
import type {
  IGetSettingsLabelsListActionPayload,
  ISettingsLabelsItem,
} from '../settingsLabelsTypes';
import {
  SettingsLabelsContentStyled,
  SettingsLabelsListStyled,
} from './styled/SettingsLabels';

export type TSettingsLabelsProps = IConnectedProps & IDispatchProps;

const SettingsLabels = (props: TSettingsLabelsProps) => {
  const [searchLabel, setSearchLabel] = useState('');
  const listBodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const loadList = debounce((searchLabel: string, start?: boolean) => {
    props.getLabelsList({
      search: searchLabel,
      start: start ?? false,
    });
  }, DEBOUNCE_DEFAULT_DELAY);

  useEffect(() => {
    loadList(searchLabel, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLabel]);

  return (
    <PageSettingsContentStyled>
      <SettingsLabelsContentStyled data-module="settingsLabels">
        <PageSettingsHeaderStyled>
          {props.settingsTranslation.settingsLabels.title}
        </PageSettingsHeaderStyled>

        <LabelEditorSearch
          createLabel={props.addLabel}
          isLabelsImportEnabled={props.isLabelsImportEnabled}
          isLoading={props.isLoading}
          searchLabel={setSearchLabel}
          searchValue={searchLabel}
          translation={props.commonTranslation.labelEditor}
        />

        <InfinityScrollWrapper
          hasMore={props.hasMore}
          sizeElement={listBodyRef}
          watchDocumentBody={true}
          onScrollEvent={() => loadList(searchLabel)}
        >
          <SettingsLabelsListStyled ref={listBodyRef}>
            <LabelEditorList
              isLoading={props.isLoading}
              labels={props.labelsList}
              removeLabel={props.removeLabel}
              searchValue={''}
              translation={props.commonTranslation.labelEditor}
            />
          </SettingsLabelsListStyled>
        </InfinityScrollWrapper>
      </SettingsLabelsContentStyled>
    </PageSettingsContentStyled>
  );
};

export interface IConnectedProps {
  isLabelsImportEnabled: boolean;
  commonTranslation: ICommonTranslation;
  isLoading: boolean;
  hasMore: boolean;
  labelsList: ISettingsLabelsItem[];
  settingsTranslation: IPageSettingsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>(
  {
    commonTranslation: makeSelectTranslationCommon(),
    isLoading: makeSelectSettingsLabelsIsLoading(),
    isLabelsImportEnabled: makeSelectLabelsImportButtonEnabled(),
    hasMore: makeSelectSettingsLabelsListHasMore(),
    labelsList: makeSelectSettingsLabelsList(),
    settingsTranslation: makeSelectTranslationPageSettings(),
  },
);

interface IDispatchProps {
  getLabelsList: (payload: IGetSettingsLabelsListActionPayload) => void;
  addLabel: (n: string) => void;
  removeLabel: (n: number) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  getLabelsList: (payload) => dispatch(getSettingsLabelsListAction(payload)),
  addLabel: (payload) => dispatch(addSettingsLabelAction(payload)),
  removeLabel: (payload) => dispatch(removeSettingsLabelAction(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(memo(SettingsLabels));
